/**
 * PDP funnel tracking helper — Meta Pixel custom events only.
 *
 * Scope: this file adds NO new tracking mechanism and does not touch
 * the base Meta Pixel, ad settings, or any existing standard event
 * (ViewContent / AddToCart / InitiateCheckout / Purchase). Those
 * continue to originate exactly as before (Shopify's native
 * "Facebook & Instagram" Sales Channel integration — there is no
 * custom pixel code in this theme to duplicate, and this file never
 * initializes a pixel of its own).
 *
 * This helper exists only so the three PDP builders (Solo, Matching,
 * Fabric) share one tiny, reviewed wrapper around Meta's own
 * `fbq('trackCustom', ...)` API instead of each re-implementing the
 * same few lines. Each builder is still fully responsible for its
 * own "fire once per page visit" guard and its own trigger logic —
 * this file has no opinion on *when* an event should fire, only on
 * *how* it reaches fbq once a builder has decided to fire it.
 *
 * Bounded delivery window: on a page where `fbq` has not finished
 * loading yet (native pixel script still in flight), an event is
 * held in a small in-memory queue and retried on a fixed short
 * interval until `fbq` becomes available, up to MAX_WAIT_MS total —
 * after which anything still queued is dropped. This is a page-load
 * safety net only:
 *  - In-memory only. No localStorage/sessionStorage/cookies, so
 *    nothing survives or leaks across page loads.
 *  - Bounded: a plain setInterval capped at MAX_WAIT_MS, cleared the
 *    moment the queue drains or the deadline passes — never an
 *    unbounded poll.
 *  - `fire()` calls `fbq` at most once per event, whether that call
 *    happens immediately or after a short wait, so no event can be
 *    delivered to Meta twice through this helper.
 *  - Never reads or writes PDP state, cart payloads, line item
 *    properties, or validation.
 *
 * Return contract (this is what callers must key their own
 * fire-once flag on): `fire()` returns `true` only when the event
 * was either handed to `fbq` immediately or safely accepted into the
 * bounded pending queue for later delivery. It returns `false` when
 * the event could not be handled at all (e.g. the defensive queue
 * cap was hit) — callers must NOT treat a `false` return as
 * "consumed," so a later real interaction gets a fair retry.
 */
window.PTFunnelTracking = (function () {
  'use strict';

  var MAX_WAIT_MS = 8000; // total bounded window to wait for fbq
  var POLL_INTERVAL_MS = 250; // fixed retry cadence within that window
  var MAX_QUEUE_SIZE = 10; // defensive cap; in practice at most 2 events/page

  var pending = [];
  var pollTimer = null;
  var waitedMs = 0;

  function sendNow(eventName, params) {
    try {
      window.fbq('trackCustom', eventName, params || {});
    } catch (err) {
      // fbq existed but threw internally -- out of our control and
      // not something a retry could fix, so we do not requeue it.
    }
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    waitedMs = 0;
  }

  function flushPending() {
    while (pending.length) {
      var item = pending.shift();
      sendNow(item.eventName, item.params);
    }
    stopPolling();
  }

  function startPolling() {
    if (pollTimer) return; // already polling -- one shared timer for all queued events
    pollTimer = setInterval(function () {
      waitedMs += POLL_INTERVAL_MS;
      if (typeof window.fbq === 'function') {
        flushPending();
        return;
      }
      if (waitedMs >= MAX_WAIT_MS) {
        // Bounded window exhausted -- give up rather than polling
        // forever. Whatever is still queued is dropped.
        pending = [];
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  }

  function fire(eventName, params) {
    if (typeof window.fbq === 'function') {
      sendNow(eventName, params);
      return true;
    }
    if (pending.length >= MAX_QUEUE_SIZE) return false;
    pending.push({ eventName: eventName, params: params || {} });
    startPolling();
    return true;
  }

  return { fire: fire };
})();
